import React from 'react';
import { Input, rem } from '@mantine/core';
import { RichTextEditor as MantineRichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';

export interface IRichTextEditorProps {
  label: string;
  /** HTML content. */
  value: string;
  onChange: (html: string) => void;
  description?: React.ReactNode;
  /** Minimum height of the editable area, in px. */
  minHeight?: number;
}

const ALLOWED_LINK_SCHEME = /^(https?|mailto):/i;
const MAX_UNECHOED = 100;

const extensions = [
  StarterKit.configure({
    heading: { levels: [2] },
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
  }),
  Link.configure({
    openOnClick: false,
    isAllowedUri: (url) => ALLOWED_LINK_SCHEME.test(url.trim()),
  }),
];

/** @registry Labelled rich text (HTML) editor limited to bold, italic, bullet/ordered lists, and links; use for email bodies and other short formatted text. Loading a new `value` does not call `onChange`. */
export const RichTextEditor: React.FC<IRichTextEditorProps> = ({
  label,
  value,
  onChange,
  description,
  minHeight = 160,
}) => {
  const id = React.useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const unechoedRef = React.useRef<string[]>([]);

  const editor = useEditor({
    extensions,
    content: value,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId,
        ...(description ? { 'aria-describedby': descriptionId } : {}),
        style: `min-height: ${rem(minHeight)}`,
      },
    },
    onUpdate: ({ editor: current }) => {
      const html = current.getHTML();
      const unechoed = unechoedRef.current;
      unechoed.push(html);
      if (unechoed.length > MAX_UNECHOED) unechoed.shift();
      onChangeRef.current(html);
    },
  });

  // The caller's echo of an emitted value can arrive after later keystrokes
  // have landed; applying it would drop them. setContent must not emit, or
  // loading a value would read to the caller as a user edit.
  React.useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const unechoed = unechoedRef.current;
    const echoIndex = unechoed.indexOf(value);
    if (echoIndex >= 0) {
      unechoed.splice(0, echoIndex + 1);
      return;
    }
    unechoed.length = 0;
    if (value !== editor.getHTML()) editor.commands.setContent(value, false);
  }, [editor, value]);

  return (
    <Input.Wrapper
      id={id}
      label={label}
      labelElement="div"
      description={description}
    >
      <MantineRichTextEditor editor={editor}>
        <MantineRichTextEditor.Toolbar>
          <MantineRichTextEditor.ControlsGroup>
            <MantineRichTextEditor.Bold />
            <MantineRichTextEditor.Italic />
          </MantineRichTextEditor.ControlsGroup>
          <MantineRichTextEditor.ControlsGroup>
            <MantineRichTextEditor.BulletList />
            <MantineRichTextEditor.OrderedList />
          </MantineRichTextEditor.ControlsGroup>
          <MantineRichTextEditor.ControlsGroup>
            <MantineRichTextEditor.Link />
            <MantineRichTextEditor.Unlink />
          </MantineRichTextEditor.ControlsGroup>
        </MantineRichTextEditor.Toolbar>
        <MantineRichTextEditor.Content />
      </MantineRichTextEditor>
    </Input.Wrapper>
  );
};
